import { Sort } from '@angular/material/sort';

export function sortEquals(a: Sort, b: Sort): boolean {
  return a === b || a && b && (a.active === b.active && a.direction === b.direction);
}

export function convertSortToApiParam(
  sort: Sort,
  defaultValue?: string,
  { defaultDirection, defaultWhenDirectionless = true, convertMap }: {
    defaultDirection?: 'asc' | 'desc'; defaultWhenDirectionless?: boolean; convertMap?: Record<string, string>;
  } = {},
) {
  if (!sort) return defaultValue;

  if (!sort.direction && defaultWhenDirectionless && defaultValue != null) return defaultValue;

  if (!defaultDirection && defaultValue) {
    defaultDirection = defaultValue.startsWith('_') ? 'desc' : 'asc';
  }

  const sortName = convertMap?.[sort.active] || sort.active;
  return `${(sort.direction || defaultDirection) === 'desc' ? '_' : ''}${sortName}`;
}

/** Parses API sort strings (`field` asc / `_field` desc). Uses the first field of comma-separated sorts. */
export function parseApiSortParam(sort?: string | null): { key: string, direction: 'asc' | 'desc' } | null {
  if (!sort) {
    return null;
  }
  const primary = sort.split(',')[0];
  const direction = primary.startsWith('_') ? 'desc' : 'asc';
  const key = primary.startsWith('_') ? primary.slice(1) : primary;
  return key ? { key, direction } : null;
}

/** Live API sort keys that mock rows expose under a different property. */
const API_SORT_KEY_ALIASES: Record<string, string> = {
  name: 'userName',
  time: 'total',
};

/**
 * Resolves a sortable field value, including API shapes like `{ value: iso }` used for week_start.
 */
export function resolveApiSortValue(item: unknown, key: string): unknown {
  if (item == null || typeof item !== 'object') {
    return undefined;
  }
  const record = item as Record<string, unknown>;
  const value = record[key] ?? record[API_SORT_KEY_ALIASES[key]];
  if (value != null && typeof value === 'object' && 'value' in (value )) {
    return (value as { value: unknown }).value;
  }
  return value;
}

export function compareByApiSortParam(a: unknown, b: unknown, sort?: string | null): number {
  const parsed = parseApiSortParam(sort);
  if (!parsed) {
    return 0;
  }

  const valueA = resolveApiSortValue(a, parsed.key);
  const valueB = resolveApiSortValue(b, parsed.key);
  const sign = parsed.direction === 'asc' ? 1 : -1;

  if (valueA == null && valueB == null) {
    return 0;
  }
  if (valueA == null) {
    return -1 * sign;
  }
  if (valueB == null) {
    return 1 * sign;
  }

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return (valueA - valueB) * sign;
  }

  if (typeof valueA === 'string' && typeof valueB === 'string') {
    const looksLikeDate = /^\d{4}-\d{2}-\d{2}/.test(valueA) && /^\d{4}-\d{2}-\d{2}/.test(valueB);
    if (looksLikeDate) {
      return (Date.parse(valueA) - Date.parse(valueB)) * sign;
    }
    return valueA.localeCompare(valueB) * sign;
  }

  if (valueA > valueB) {
    return 1 * sign;
  }
  if (valueA < valueB) {
    return -1 * sign;
  }
  return 0;
}

/** Returns a new array sorted by an API sort param (`field` / `_field`). */
export function sortByApiParam<T>(items: T[], sort?: string | null): T[] {
  if (!items?.length || !parseApiSortParam(sort)) {
    return items ? [...items] : [];
  }
  return [...items].sort((a, b) => compareByApiSortParam(a, b, sort));
}
